import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { OneTrustContextType } from '../types';

/**
 * OneTrust consent category ID for "Performance Cookies" (analytics/tracking).
 * OneTrust serializes active groups as a comma-delimited string, e.g. ",1,2,3,".
 * We wrap the ID in commas to avoid false-positive substring matches (e.g. ",12,"
 * accidentally matching a check for "2").
 *
 * If your OneTrust configuration uses a different category ID for analytics,
 * update this constant. Category IDs are found in your OneTrust admin console
 * under: Cookie Compliance > Categorizations.
 */
const ONETRUST_ANALYTICS_CATEGORY = ',2,';

const OneTrustContext = createContext<OneTrustContextType | null>(null);

/**
 * Custom hook to return OneTrustContext.
 */
export const useOneTrustContext = (): OneTrustContextType => {
  const context = useContext(OneTrustContext);
  if (context === null) {
    throw new Error(
      'calls to useOneTrustContext must be wrapped in <OneTrustProvider>',
    );
  }
  return context;
};

/**
 * Check `window.OnetrustActiveGroups` every quarter of a second to see if OneTrust has been initialized.
 * OneTrust script is referenced from the head HTML tag and sets `window.OnetrustActiveGroups`
 * and `OptanonConsent` cookie once it has been initialized.
 */
export const OneTrustProvider = ({ children }: PropsWithChildren) => {
  const [analyticsOptOut, setAnalyticsOptOut] = useState(true);
  const [onetrustActiveGroups, setOnetrustActiveGroups] = useState<
    string | undefined
  >(typeof window !== 'undefined' ? window.OnetrustActiveGroups : undefined);

  // Store the interval ID in a ref so it is always current inside the closure,
  // avoiding the stale-variable bug that occurred when reassigning a `let` that
  // the cleanup function had already captured.
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );

  useEffect(() => {
    const checkOnetrustActiveGroups = () => {
      if (window.OnetrustActiveGroups === undefined) {
        return;
      }

      setOnetrustActiveGroups(window.OnetrustActiveGroups);
      // React batches identical state updates as a no-op, so no need to
      // compare before calling the setter.
      setAnalyticsOptOut(
        !window.OnetrustActiveGroups.includes(ONETRUST_ANALYTICS_CATEGORY),
      );

      /**
       * Clear the interval once OneTrust has been initialized and only recheck
       * every 1 sec if the opt-out status is still the default (true).
       */
      clearInterval(intervalRef.current);
      if (!window.OnetrustActiveGroups.includes(ONETRUST_ANALYTICS_CATEGORY)) {
        intervalRef.current = setInterval(checkOnetrustActiveGroups, 1000);
      }
    };

    // Start the initial 250ms poll. The effect runs once on mount only.
    intervalRef.current = setInterval(checkOnetrustActiveGroups, 250);
    return () => {
      clearInterval(intervalRef.current);
    };
    // Empty deps array is intentional: this effect sets up a one-time interval on
    // mount. State setters (setAnalyticsOptOut, setOnetrustActiveGroups) have
    // stable identities guaranteed by React, and intervalRef is a stable ref
    // container — so listing them would cause unnecessary re-subscriptions.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OneTrustContext.Provider value={{ analyticsOptOut, onetrustActiveGroups }}>
      {children}
    </OneTrustContext.Provider>
  );
};
